import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as calendarService from '../../../modules/CalendarService'
import EventInstance from '../../../modules/EventInstance'
import { getTodoInstances } from '../../../modules/TodoUtils'
import styles from './SchedulingDayBox.module.scss'

import i18n from 'i18n'
import { useFormatSettings } from 'pages/todo/components/TodoList/TodoSectionListView/hooks/useFormatSettings'
import settings from 'popups/Settings'
import DayAllday from '../../DayAllday'
import DayHeader from '../../DayHeader'
import PeriodView from '../../PeriodView'
import TimeLineView from '../../TimeLineView'
import TimeMark from '../../TimeMark'
import TimezoneNameBlock from '../../TimezoneNameBlock'
import SelectionRect from './components/SelectionRect'

import useTimelineInteraction from './hooks/useTimelineInteraction'
import type { BusyTimes } from './types'
import type { SelectionTimeRange } from './utils/selectionHelpers'
import { getActiveSelection } from './utils/selectionHelpers'

import {
  formatTimeWithDate,
  getDayBounds,
  getInteractionZone,
  getSelectionForDate,
  hasConflict,
  mergeBusyTimes,
} from './utils/SchedulingUtils'
import { hasConflict as hasAttendeeConflict } from './utils/conflictDetection'
import { calculateScrollPosition } from './utils/scrollHelpers'

export {
  UPDATE_INTERVAL,
  SLOT_HEIGHT,
  MIN_SELECTION_MINUTES,
  MAX_SELECTION_MINUTES,
  HANDLE_SIZE,
  CLICK_THRESHOLD,
  DEFAULT_SCROLL_POSITION,
  SCROLL_OFFSET,
  MINUTES_PER_SLOT,
  ADJACENT_MERGE_THRESHOLD,
} from './schedulingConstants'
import {
  UPDATE_INTERVAL,
  SLOT_HEIGHT,
  DEFAULT_SCROLL_POSITION,
  MINUTES_PER_SLOT,
} from './schedulingConstants'

interface SelectionTimeRangeWithConflict extends SelectionTimeRange {
  hasConflict?: boolean
}

interface BusyBlock {
  start: Date
  end: Date
}

/**
 * Scheduling selection pseudo-event for DayAllday display
 */
interface SchedulingSelectionItem {
  startDate: Date
  endDate: Date
  allday: boolean
  subject: string
  color: string
  isSchedulingSelection: boolean
  id: string
  isBusy: boolean
  isCanceled: boolean
  isDeclined: boolean
  isMeeting: boolean
  myMeetingResponse: null
  isMyEvent: boolean
}

interface SchedulingDayBoxProps {
  currentDate: Date
  hideTodos?: boolean
  attendeeBusyTimes?: BusyTimes
  onTimeSelect?:
    | ((start: Date | null, end: Date | null, allday: boolean, index?: number) => void)
    | null
  attendeesCount?: number
  eventTitle?: string
  selections?: SelectionTimeRangeWithConflict[]
  multiMode?: boolean
  currentDuration?: number | null
  onOwnerConflictChange?: ((hasConflict: boolean) => void) | null
  scrollTargetTime?: Date | null
  activeTab?: string
  preserveScroll?: boolean
}

const SchedulingDayBox = ({
  currentDate,
  hideTodos = true,
  attendeeBusyTimes = {},
  onTimeSelect = null,
  attendeesCount = 0,
  eventTitle = '',
  selections: selectionsProp = [],
  multiMode = false,
  currentDuration = null,
  onOwnerConflictChange = null,
  scrollTargetTime = null,
  activeTab,
  preserveScroll = false,
}: SchedulingDayBoxProps) => {
  if (!currentDate) {
    return null
  }

  // ========================================================================
  // Settings Management - Reactive to changes
  // ========================================================================
  const { timeFormat, dateFormat } = useFormatSettings()
  const [locale, setLocale] = useState(() => settings.getValue('general').locale)
  const [isMobileView, setIsMobileView] = useState(
    () => settings.getStore().getState().appsettings.isMobileView,
  )

  // Subscribe to locale changes
  useEffect(() => {
    const handleSettingsChange = () => {
      const newLocale = settings.getValue('general').locale
      if (newLocale !== locale) {
        setLocale(newLocale)
      }
    }

    const unsubscribe = settings.getStore().subscribe(handleSettingsChange)

    return () => {
      unsubscribe()
    }
  }, [locale])

  // Subscribe to mobile view changes
  useEffect(() => {
    const handleSettingsChange = () => {
      const newIsMobileView = settings.getStore().getState().appsettings.isMobileView
      if (newIsMobileView !== isMobileView) {
        setIsMobileView(newIsMobileView)
      }
    }

    const unsubscribe = settings.getStore().subscribe(handleSettingsChange)

    return () => {
      unsubscribe()
    }
  }, [isMobileView])

  // ========================================================================
  // State Management
  // ========================================================================
  const [ownerEvents, setOwnerEvents] = useState<EventInstance[]>([])
  const [mergedBusyBlocks, setMergedBusyBlocks] = useState<BusyBlock[]>([])
  const [alldayEvents, setAlldayEvents] = useState<EventInstance[]>([])
  const [currentTime, setCurrentTime] = useState(new Date())
  const [isToday, setIsToday] = useState(false)

  // Refs
  const dayBoxRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const timeLineContainerRef = useRef<HTMLDivElement>(null)
  const headerBlockRef = useRef<HTMLDivElement>(null)
  const busyInstances = useRef<{ startDate: Date; endDate: Date }[]>([])
  const prevDateRef = useRef<string | null>(null) // 이전 날짜를 추적용(날짜의 실제 값 변경만 감지)
  const prevActiveTabRef = useRef<string | undefined>(activeTab)

  // ========================================================================
  // Selection State - Using helper utilities
  // ========================================================================

  // Get active selection for current interaction
  // In multi-mode: returns array from prop
  // In single-mode: returns array with single selection (or temp drag state)
  const activeSelections = useMemo(() => {
    return getActiveSelection(
      selectionsProp,
      null, // tempDragState managed by hook
      false, // isDragging managed by hook
      multiMode,
    )
  }, [selectionsProp, multiMode])

  // For single-mode rendering
  const selectedTimeRange = multiMode ? null : activeSelections[0] || null

  // For multi-mode rendering
  const selectedTimeRanges = multiMode ? activeSelections : []

  // 날짜 범위 문자열 추출 (loadOwnerEvents 의존성 배열용)
  // 날짜만 비교하여 시간 변경 시에는 재실행되지 않도록 함
  const selectionDateRangeKey = useMemo(() => {
    if (multiMode) {
      if (selectedTimeRanges.length === 0) return ''
      const dateStrings = selectedTimeRanges
        .map((s) => {
          if (!s?.start || !s?.end) return null
          const startDate = new Date(s.start).toDateString()
          const endDate = new Date(s.end).toDateString()
          return `${startDate}|${endDate}`
        })
        .filter(Boolean)
      return dateStrings.join(',')
    } else {
      if (!selectedTimeRange?.start || !selectedTimeRange?.end) return ''
      const startDate = new Date(selectedTimeRange.start).toDateString()
      const endDate = new Date(selectedTimeRange.end).toDateString()
      return `${startDate}|${endDate}`
    }
  }, [multiMode, selectedTimeRange, selectedTimeRanges])

  // ========================================================================
  // Conflict Detection Helper
  // ========================================================================

  const checkSelectionConflict = useCallback(
    (start: Date, end: Date, allday?: boolean) => {
      // Events 에서 busy 상태인 경우의 start, end 를 추출 (startDate, endDate 를 start, end 로 변환 - time range 로 사용)
      const busyOwnerEvents = [...ownerEvents, ...alldayEvents]
        .filter((event) => event.isBusy === true)
        .map((event) => ({
          start: event.startDate,
          end: event.endDate,
          allday: event.allday,
        }))

      const busyInstancesArray = mergedBusyBlocks.map((block) => ({
        start: block.start,
        end: block.end,
        allday: false,
      }))
      return hasConflict(start, end, busyInstancesArray, busyOwnerEvents, allday)
    },
    [ownerEvents, alldayEvents, mergedBusyBlocks],
  )

  // ========================================================================
  // Timeline Interaction Hook
  // ========================================================================

  // Wrapper for checkSelectionConflict that handles allday parameter
  // Timeline interactions are always for timed selections (not all-day)
  const checkTimedConflict = useCallback(
    (start: Date, end: Date) => {
      return checkSelectionConflict(start, end, false) // false = not all-day
    },
    [checkSelectionConflict],
  )

  const {
    interactionMode,
    tempSelectionArea,
    tempDragState,
    activeSelectionIndex,
    onTimelineMouseDown,
    onTimelineMouseMove,
    onTimelineMouseUp,
    onTimelineTouchStart,
    onTimelineTouchMove,
    onTimelineTouchEnd,
    onSelectionMouseDown,
    onSelectionTouchStart,
  } = useTimelineInteraction({
    timeLineContainerRef,
    startDate: currentDate,
    onSelectionChange: onTimeSelect,
    checkConflict: checkTimedConflict,
    multiMode,
    currentSelection: selectedTimeRange,
    selections: selectedTimeRanges, // Pass selections array for multi-mode
    currentDuration, // Pass current duration for click-based selection
    isMobileView, // Pass mobile view state
  })

  // Active selection with temp drag state
  const displaySelection = tempDragState || selectedTimeRange

  // 현재 시간 위치 계산
  const getCurrentTimePosition = useCallback(() => {
    const hours = currentTime.getHours()
    const minutes = currentTime.getMinutes()
    const totalMinutes = hours * 60 + minutes
    return (totalMinutes / 30) * SLOT_HEIGHT
  }, [currentTime])

  // 현재 시간 업데이트 함수
  const updateCurrentTime = useCallback(() => {
    const now = new Date()
    setCurrentTime(now)
  }, [])

  const parseIsToday = useCallback(() => {
    const today = new Date()
    const isCurrentDay =
      currentDate.getFullYear() === today.getFullYear() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getDate() === today.getDate()

    setIsToday(isCurrentDay)
    return isCurrentDay
  }, [currentDate])

  useEffect(() => {
    parseIsToday()
  }, [parseIsToday])

  /**
   * 초기 스크롤 위치 설정
   *
   * - scrollTargetTime이 제공된 경우: 해당 시간으로 스크롤 (화면 중앙)
   * - 오늘 날짜인 경우: 현재 시간 기준으로 스크롤
   * - 그 외 날짜: 기본 위치(9AM)로 스크롤
   */
  const setInitialScrollPosition = useCallback(() => {
    if (!scrollContainerRef.current) return

    let targetTime
    if (scrollTargetTime) {
      // Use custom scroll target (for edit mode)
      targetTime = scrollTargetTime
    } else if (parseIsToday()) {
      // Use current time for today
      targetTime = new Date()
    } else {
      // Use default position for other dates
      scrollContainerRef.current.scrollTop = DEFAULT_SCROLL_POSITION
      return
    }

    // Calculate scroll position using helper function
    const containerHeight = scrollContainerRef.current.clientHeight
    const scrollPosition = calculateScrollPosition(targetTime, containerHeight)
    scrollContainerRef.current.scrollTop = scrollPosition
  }, [scrollTargetTime, parseIsToday])

  /**
   * 소유자의 캘린더 이벤트 로드
   *
   * 현재 보고 있는 날짜(`date`)와 selection 범위를 합쳐서 query range를 구성.
   * 이렇게 하면 현재 날짜의 이벤트와 selection 범위의 이벤트를 모두 조회
   * 조회 후에는 현재 날짜(`date`)에 해당하는 events만 필터링하여 반환
   *
   * @returns {Promise<Array>} 현재 날짜에 해당하는 타임라인 이벤트 배열
   */
  const loadOwnerEvents = useCallback(async () => {
    // startDate가 없으면 빈 배열 반환
    if (!currentDate) {
      setAlldayEvents([])
      return []
    }

    // 현재 날짜의 경계 계산
    const { dayStart: currentDayStart, dayEnd: currentDayEnd } = getDayBounds(currentDate)

    // 조회할 시간 범위 계산
    // 현재 보고 있는 날짜와 selection 범위를 합쳐서 query range 구성
    let queryDayStart = currentDayStart
    let queryDayEnd = currentDayEnd

    // multi-mode와 single-mode 모두 처리
    const selectionsToCheck = multiMode
      ? selectedTimeRanges
      : selectedTimeRange
        ? [selectedTimeRange]
        : []

    if (selectionsToCheck.length > 0) {
      // 모든 selection의 날짜 범위를 합쳐서 query range 구성
      let minStartDate: Date | null = null
      let maxEndDate: Date | null = null

      selectionsToCheck.forEach((selection) => {
        if (selection && selection.start && selection.end) {
          const selectionStartDate = new Date(selection.start)
          selectionStartDate.setHours(0, 0, 0, 0)
          const selectionEndDate = new Date(selection.end)
          selectionEndDate.setHours(23, 59, 59, 999)

          if (minStartDate === null || selectionStartDate < minStartDate) {
            minStartDate = selectionStartDate
          }
          if (maxEndDate === null || selectionEndDate > maxEndDate) {
            maxEndDate = selectionEndDate
          }
        }
      })

      if (minStartDate && maxEndDate) {
        // 현재 날짜와 selection 범위를 합쳐서 query range 구성
        // 시작: min(currentDayStart, minStartDate)
        // 종료: max(currentDayEnd, maxEndDate)
        queryDayStart = currentDayStart < minStartDate ? currentDayStart : minStartDate
        queryDayEnd = currentDayEnd > maxEndDate ? currentDayEnd : maxEndDate
      }
    }

    // 전체 범위에서 이벤트 조회
    const instances = calendarService.getInstances()
    const alldayInstances: EventInstance[] = []
    const noAllDayInstances: EventInstance[] = []

    // Query 범위에 해당하는 모든 이벤트 수집
    instances.forEach((instance) => {
      if (
        instance.startDate < queryDayEnd &&
        instance.endDate.valueOf() - 1 >= queryDayStart.valueOf()
      ) {
        if (instance.allday) {
          alldayInstances.push(instance)
        } else {
          noAllDayInstances.push(instance)
        }
      }
    })

    // Todo 인스턴스 추가 (hideTodos가 false일 때만)
    if (!hideTodos) {
      const todoInstances = await getTodoInstances(queryDayStart, queryDayEnd)
      if (todoInstances.length > 0) {
        alldayInstances.push(...todoInstances)
      }
    }

    // 현재 날짜에 해당하는 events만 필터링
    const filteredAlldayEvents = alldayInstances.filter((instance) => {
      return (
        instance.startDate < currentDayEnd &&
        instance.endDate.valueOf() - 1 >= currentDayStart.valueOf()
      )
    })

    const filteredTimelineEvents = noAllDayInstances.filter((instance) => {
      return (
        instance.startDate < currentDayEnd &&
        instance.endDate.valueOf() - 1 >= currentDayStart.valueOf()
      )
    })

    // Allday 이벤트 정렬
    filteredAlldayEvents.sort((a, b) => {
      if (a.todo && !b.todo) {
        return 1
      }
      if (b.todo && !a.todo) {
        return -1
      }
      if (a.todo?.status === 'COMPLETED') {
        return 1
      }
      if (b.todo?.status === 'COMPLETED') {
        return -1
      }
      const sOffset = a.startDate.getTime() - b.startDate.getTime()
      if (sOffset !== 0) {
        return sOffset
      }
      const eOffset = b.endDate.getTime() - a.endDate.getTime()
      if (eOffset !== 0) {
        return eOffset
      }
      const aSubject = a.subject || ''
      const bSubject = b.subject || ''
      return aSubject.localeCompare(bSubject, locale)
    })

    setAlldayEvents(filteredAlldayEvents)
    return filteredTimelineEvents
  }, [
    currentDate,
    locale,
    hideTodos,
    multiMode,
    selectionDateRangeKey, // 날짜가 변경될 때만 reload (시간만 변경되는 경우는 제외)
    // selectedTimeRange와 selectedTimeRanges는 함수 내부에서 사용되지만,
    // selectionDateRangeKey가 변경될 때만 재실행되도록 안정화됨
  ])

  /**
   * 참석자의 바쁜 시간을 현재 날짜 기준으로 병합하여 표시용으로 변환
   *
   * 여러 날에 걸친 selection이 있어도 현재 날짜(`date`)에 해당하는 busy blocks만 반환합니다.
   * 충돌 감지를 위해 busyInstances ref에도 저장합니다.
   *
   * @returns {Array} 현재 날짜에 해당하는 병합된 busy blocks 배열
   */
  const mergeBusyTimesForDisplay = useCallback(() => {
    // startDate가 없으면 빈 배열 반환
    if (!currentDate) {
      return []
    }

    const merged = mergeBusyTimes(attendeeBusyTimes, currentDate)
    // 충돌 감지를 위해 pseudo-instance 형식으로 저장
    busyInstances.current = merged.map((block) => ({
      startDate: block.start,
      endDate: block.end,
    }))
    return merged
  }, [attendeeBusyTimes, currentDate])

  /**
   * 타임라인에 표시할 이벤트 목록
   *
   * 소유자의 이벤트만 포함하며, busy blocks는 별도 레이어로 렌더링됩니다.
   */
  const timeline = useMemo(() => {
    return ownerEvents
  }, [ownerEvents])

  // Helper for getAllOwnerEvents (needed by getConflictMessage)
  const getAllOwnerEvents = useCallback(() => {
    return [...ownerEvents, ...alldayEvents]
  }, [ownerEvents, alldayEvents])

  // 날짜 변경 시 소유자 이벤트 로드
  useEffect(() => {
    const loadData = async () => {
      const timelineEvents = await loadOwnerEvents()
      setOwnerEvents(timelineEvents)
    }
    loadData()
  }, [loadOwnerEvents])

  // 참석자 바쁜 시간 변경 시 병합된 busy blocks 업데이트
  useEffect(() => {
    const merged = mergeBusyTimesForDisplay()
    setMergedBusyBlocks(merged)
  }, [mergeBusyTimesForDisplay])

  // 소유자 충돌 상태 업데이트 및 상위 컴포넌트로 전달
  useEffect(() => {
    if (!onOwnerConflictChange) return

    // Owner conflict should ONLY check owner's events, NOT attendee busy times
    // Filter to only include owner events where isBusy is true
    const busyOwnerEvents = [...ownerEvents, ...alldayEvents].filter(
      (event) => event.isBusy === true,
    )

    // Multi-mode: Check if ANY of the selectedTimeRanges has conflict
    if (multiMode) {
      // 선택된 시간이 없으면 충돌 없음
      if (!selectedTimeRanges || selectedTimeRanges.length === 0) {
        onOwnerConflictChange(false)
        return
      }

      // Check if ANY selection conflicts with owner's events
      const hasAnyConflict = selectedTimeRanges.some((selection) => {
        if (!selection || !selection.start || !selection.end) return false

        const { start, end, allday } = selection

        // For all-day selections, convert to full day time range for consistent conflict checking
        let checkStart = start
        let checkEnd = end

        if (allday) {
          checkStart = new Date(start)
          checkStart.setHours(0, 0, 0, 0)
          checkEnd = new Date(end)
          checkEnd.setHours(23, 59, 59, 999)
        }

        return busyOwnerEvents.some((event) => {
          return checkStart < event.endDate && checkEnd > event.startDate
        })
      })

      onOwnerConflictChange(hasAnyConflict)
    } else {
      // Single-mode: Check single selectedTimeRange
      // 선택된 시간이 없으면 충돌 없음
      if (!selectedTimeRange || !selectedTimeRange.start || !selectedTimeRange.end) {
        onOwnerConflictChange(false)
        return
      }

      const { start, end, allday } = selectedTimeRange

      // For all-day selections, convert to full day time range for consistent conflict checking
      let checkStart = start
      let checkEnd = end

      if (allday) {
        checkStart = new Date(start)
        checkStart.setHours(0, 0, 0, 0)
        checkEnd = new Date(end)
        checkEnd.setHours(23, 59, 59, 999)
      }

      const ownerConflict = busyOwnerEvents.some((event) => {
        return checkStart < event.endDate && checkEnd > event.startDate
      })

      // 상위 컴포넌트로 소유자 충돌 상태 전달
      onOwnerConflictChange(ownerConflict)
    }
  }, [
    multiMode,
    selectedTimeRange,
    selectedTimeRanges,
    ownerEvents,
    alldayEvents,
    onOwnerConflictChange,
  ])

  // 현재 시간 업데이트
  useEffect(() => {
    updateCurrentTime()
    timerRef.current = setInterval(updateCurrentTime, UPDATE_INTERVAL)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [updateCurrentTime])

  // Document-level event listeners for drag operations
  // This ensures mouse/touch events are captured even when cursor is over the selection overlay
  useEffect(() => {
    if (!interactionMode) return

    const handleDocumentMouseMove = (e: MouseEvent) => {
      onTimelineMouseMove(e)
    }

    const handleDocumentMouseUp = (e: MouseEvent) => {
      onTimelineMouseUp(e)
    }

    const handleDocumentTouchMove = (e: TouchEvent) => {
      // Mobile: Prevent scroll when dragging/resizing selection rect
      // Don't stop propagation - onTimelineTouchMove handles the event
      if (isMobileView && ['moving', 'resizing-top', 'resizing-bottom'].includes(interactionMode)) {
        e.preventDefault()
        // Don't call stopPropagation() - onTimelineTouchMove needs to process the event
      }
      onTimelineTouchMove(e)
    }

    const handleDocumentTouchEnd = (e: TouchEvent) => {
      // Mobile: Prevent default when ending selection rect drag/resize
      if (isMobileView && ['moving', 'resizing-top', 'resizing-bottom'].includes(interactionMode)) {
        e.preventDefault()
        e.stopPropagation()
      }
      onTimelineTouchEnd(e)
    }

    // Add listeners when interaction mode is active
    document.addEventListener('mousemove', handleDocumentMouseMove)
    document.addEventListener('mouseup', handleDocumentMouseUp)
    document.addEventListener('touchmove', handleDocumentTouchMove, { passive: false })
    document.addEventListener('touchend', handleDocumentTouchEnd)

    // Clean up listeners
    return () => {
      document.removeEventListener('mousemove', handleDocumentMouseMove)
      document.removeEventListener('mouseup', handleDocumentMouseUp)
      document.removeEventListener('touchmove', handleDocumentTouchMove)
      document.removeEventListener('touchend', handleDocumentTouchEnd)
    }
  }, [interactionMode, isMobileView])

  // Scroll container touch event listeners - prevent scroll when dragging selection rect
  useEffect(() => {
    if (!scrollContainerRef.current) return
    if (!isMobileView) return

    const scrollContainer = scrollContainerRef.current

    const handleScrollContainerTouchMove = (e: TouchEvent) => {
      // Prevent scroll when dragging/resizing selection rect
      // Don't stop propagation - let event continue to timeline handlers
      if (
        interactionMode &&
        ['moving', 'resizing-top', 'resizing-bottom'].includes(interactionMode)
      ) {
        e.preventDefault()
        // Don't call stopPropagation() - let event bubble to timeline handlers
      }
    }

    scrollContainer.addEventListener('touchmove', handleScrollContainerTouchMove, {
      passive: false,
    })

    return () => {
      scrollContainer.removeEventListener('touchmove', handleScrollContainerTouchMove)
    }
  }, [interactionMode, isMobileView])

  // Set scroll position - only on initial mount and tab switch
  // 좌우 화살표 네비게이션(preserveScroll=true)일 때는 스크롤 유지
  useEffect(() => {
    const currentDateStr = dayjs(currentDate).format('YYYY-MM-DD')

    // 날짜가 실제로 변경된 경우, preserveScroll이 아닐 때만 스크롤 실행
    if (prevDateRef.current !== currentDateStr) {
      if (!preserveScroll) {
        setInitialScrollPosition()
      }
      prevDateRef.current = currentDateStr
    }

    // 탭이 'scheduling'으로 변경될 때 스크롤 실행
    if (activeTab === 'scheduling' && prevActiveTabRef.current !== 'scheduling') {
      setTimeout(() => {
        setInitialScrollPosition()
      }, 0)
    }
    prevActiveTabRef.current = activeTab
  }, [currentDate, setInitialScrollPosition, activeTab, scrollTargetTime, preserveScroll])

  /**
   * 충돌 메시지 생성
   *
   * 소유자와 참석자들의 conflict 상황에 따라 문구를 반환.
   *
   * @param {Date} start - 선택 시작 시간
   * @param {Date} end - 선택 종료 시간
   * @returns {string} 충돌 메시지
   */
  const getConflictMessage = useCallback(
    (start: Date, end: Date, allday?: boolean) => {
      // For all-day selections, convert to full day time range for consistent conflict checking
      let checkStart = start
      let checkEnd = end

      if (allday) {
        // Convert all-day selection to full day time range (00:00:00 to 23:59:59.999)
        checkStart = new Date(start)
        checkStart.setHours(0, 0, 0, 0)

        checkEnd = new Date(end)
        checkEnd.setHours(23, 59, 59, 999)
      }

      // 소유자의 이벤트와 충돌 확인 (all day events 포함)
      // Filter to only include events where isBusy is true
      const allOwnerEvents = getAllOwnerEvents()
      const busyOwnerEvents = allOwnerEvents.filter((event) => event.isBusy === true)
      const ownerHasConflict = busyOwnerEvents.some((event) => {
        return checkStart < event.endDate && checkEnd > event.startDate
      })

      // 각 참석자의 충돌 확인
      const conflictingAttendees: string[] = []
      Object.entries(attendeeBusyTimes).forEach(([email, busyData]) => {
        if (hasAttendeeConflict(busyData, { start: checkStart, end: checkEnd }, allday)) {
          conflictingAttendees.push(email)
        }
      })

      const attendeeConflictCount = conflictingAttendees.length

      // 충돌이 없는 경우
      if (!ownerHasConflict && attendeeConflictCount === 0) {
        return i18n.t('calendar.schedulingAssistant.everyoneIsAvailable')
      }

      // 소유자만 충돌하는 경우
      if (ownerHasConflict && attendeeConflictCount === 0) {
        return i18n.t('calendar.schedulingAssistant.youArentAvailable')
      }

      // 참석자만 충돌하는 경우
      if (!ownerHasConflict && attendeeConflictCount > 0) {
        if (attendeeConflictCount === 1) {
          return i18n.t('calendar.schedulingAssistant.onePersonIsntAvailable')
        }
        return i18n.t('calendar.schedulingAssistant.multipleOthersArentAvailable', {
          count: attendeeConflictCount,
        })
      }

      // 소유자와 참석자가 모두 충돌하는 경우
      if (ownerHasConflict && attendeeConflictCount > 0) {
        if (attendeeConflictCount === 1) {
          return i18n.t('calendar.schedulingAssistant.youAndOnePersonArentAvailable')
        }
        return i18n.t('calendar.schedulingAssistant.youAndMultipleOthersArentAvailable', {
          count: attendeeConflictCount,
        })
      }

      // 기본값 (발생하지 않아야 함)
      return i18n.t('calendar.schedulingAssistant.everyoneIsAvailable')
    },
    [getAllOwnerEvents, attendeeBusyTimes],
  )

  // ========================================================================
  // All Day Selection 관련 함수
  // ========================================================================

  /**
   * All day selection item 생성
   *
   * all day event로 선택된 경우, DayAllday 영역에 표시할 pseudo-event object 생성
   *
   * @returns {Object|null} All day selection item 또는 null
   */
  const createAllDaySelectionItem = useCallback(() => {
    if (!selectedTimeRange) return null

    const { start, end, allday } = selectedTimeRange

    // Only show in all-day area if it's an all-day selection
    if (!allday) return null

    // 현재 날짜와 겹치는 부분 계산 (renderSelectionOverlay와 동일한 로직)
    const dateSelection = getSelectionForDate(start, end, currentDate, allday)

    // 현재 날짜와 겹치지 않는 경우 표시하지 않음
    if (!dateSelection) return null

    // Always recalculate conflict state to ensure accuracy
    const conflictDetected = checkSelectionConflict(start, end, allday)

    // Conflict message 생성
    const conflictMessage = getConflictMessage(start, end, allday)

    // 날짜 형식 생성 (시간 제외, dayjs 사용)
    const startDateStr = dayjs(start).format(dateFormat)
    const endDateStr = dayjs(end).format(dateFormat)

    // 날짜가 같은지 확인 (시간 제외)
    const isSameDate = dayjs(start).format('YYYY-MM-DD') === dayjs(end).format('YYYY-MM-DD')

    // Subject: 날짜 범위 + 이벤트 제목 + conflict 상태
    let subject = isSameDate ? startDateStr : `${startDateStr} ~ ${endDateStr}`
    if (eventTitle) {
      subject += `  ${eventTitle}`
    }
    if (attendeesCount > 0) {
      let message = eventTitle ? ` - ${conflictMessage}` : ` - ${conflictMessage}`

      subject += message
    }

    // Pseudo-event object 생성 (EventCell 형식으로 표시되도록)
    const selectionItem: SchedulingSelectionItem = {
      id: 'scheduling-selection',
      startDate: start,
      endDate: end,
      subject,
      isSchedulingSelection: true,
      color: conflictDetected ? '#EF4444' : '#10BF79',
      allday: true,
      isBusy: false,
      isCanceled: false,
      isDeclined: false,
      isMeeting: false,
      myMeetingResponse: null,
      isMyEvent: false,
    }
    return selectionItem
  }, [
    selectedTimeRange,
    checkSelectionConflict,
    eventTitle,
    attendeesCount,
    currentDate,
    dateFormat,
    timeFormat,
    getConflictMessage,
  ])

  /**
   * All day events와 selection item을 합쳐서 DayAllday에 전달할 배열 생성
   */
  const getAllDayEventsWithSelection = useMemo(() => {
    const selectionItem = createAllDaySelectionItem()
    if (selectionItem) {
      return [...alldayEvents, selectionItem]
    }
    return alldayEvents
  }, [alldayEvents, createAllDaySelectionItem])

  // ========================================================================
  // Rendering Functions
  // ========================================================================

  /**
   * 드래그 중 임시 선택 영역 렌더링
   * index.js의 newEventArea 방식과 동일 - 단순 반투명 박스만 표시
   *
   * 드래그 중에는 시간 계산이나 충돌 체크 없이 픽셀 위치만으로 가볍게 렌더링
   */
  const renderTempSelectionArea = () => {
    // 드래그 중(selecting 모드)일 때만 표시
    if (!tempSelectionArea || interactionMode !== 'selecting') return null

    const { top, height } = tempSelectionArea

    return (
      <div
        className={styles.newEvent} // index.js와 동일한 className 사용
        style={{
          position: 'absolute',
          top: `${top}px`,
          left: 0,
          width: '100%',
          height: `${height}px`,
          pointerEvents: 'none', // 드래그 방해하지 않도록
        }}
      />
    )
  }

  /**
   * Render multiple selections for multi-mode
   *
   * Renders all selections from the array
   */
  const renderMultipleSelections = () => {
    if (!multiMode || selectedTimeRanges.length === 0) return null

    return selectedTimeRanges.map((selection, index) => {
      // Use tempDragState if this selection is currently being dragged/resized
      const isBeingDragged =
        interactionMode &&
        activeSelectionIndex === index &&
        tempDragState &&
        (interactionMode === 'moving' ||
          interactionMode === 'resizing-top' ||
          interactionMode === 'resizing-bottom')

      const displaySelection = isBeingDragged ? tempDragState : selection
      const { start, end, allday } = displaySelection

      // 현재 날짜에 대한 selection 부분 계산
      const dateSelection = getSelectionForDate(start, end, currentDate, allday)

      // 현재 날짜와 겹치지 않는 경우 표시하지 않음
      if (!dateSelection) return null

      const { start: displayStart, end: displayEnd } = dateSelection

      // hasConflict 계산
      const conflictDetected = checkSelectionConflict(start, end, allday)

      // 위치 및 크기 계산 (현재 날짜 기준)
      const startMinutes = displayStart.getHours() * 60 + displayStart.getMinutes()
      const endMinutes = displayEnd.getHours() * 60 + displayEnd.getMinutes()
      const durationMinutes = endMinutes - startMinutes
      const startY = (startMinutes / MINUTES_PER_SLOT) * SLOT_HEIGHT
      const height = (durationMinutes / MINUTES_PER_SLOT) * SLOT_HEIGHT

      // 시간 표시
      const timeDisplayStart = formatTimeWithDate(
        start,
        start,
        end,
        currentDate,
        dateFormat,
        timeFormat,
        true,
      )
      const timeDisplayEnd = formatTimeWithDate(
        end,
        start,
        end,
        currentDate,
        dateFormat,
        timeFormat,
        false,
      )

      // Conflict message
      const conflictMessage = attendeesCount > 0 ? getConflictMessage(start, end, allday) : ''

      // Remove handler for multi-mode
      const handleRemove = () => {
        if (onTimeSelect) {
          // Notify parent to remove this selection by passing null dates, false for allday, and index
          onTimeSelect(null, null, false, index)
        }
      }

      // Event handlers for multi-mode (drag/resize/move)
      const handleMouseDown = (e: React.MouseEvent) => {
        e.stopPropagation()
        const rect = e.currentTarget.getBoundingClientRect()
        const zone = getInteractionZone(e.clientY, rect.top, height)
        onSelectionMouseDown(e.nativeEvent, zone, index) // Pass index to identify which selection
      }

      const handleTouchStart = (e: React.TouchEvent) => {
        e.stopPropagation()
        if (e.touches.length !== 1) return
        const touch = e.touches[0]
        const rect = e.currentTarget.getBoundingClientRect()
        const zone = getInteractionZone(touch.clientY, rect.top, height)
        onSelectionTouchStart(e.nativeEvent, zone, index) // Pass index to identify which selection
      }

      return (
        <SelectionRect
          key={`selection-${index}`}
          start={start}
          end={end}
          index={index}
          multiMode={true}
          hasConflict={conflictDetected}
          eventTitle={eventTitle}
          conflictMessage={conflictMessage}
          startY={startY}
          height={height}
          timeDisplayStart={timeDisplayStart}
          timeDisplayEnd={timeDisplayEnd}
          onRemove={handleRemove}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        />
      )
    })
  }

  /**
   * Render selected time range overlay
   *
   *  1. 시간 선택 영역을 표시
   *  2. 여러 날에 걸친 selection의 경우, 각 날짜에 해당하는 부분을 표시
   *  3. 충돌 상황에 따라 문구를 표시
   */
  const renderSelectionTimeRectOverlay = () => {
    // Use displaySelection to show temp drag state or current selection
    if (!displaySelection) return null

    const { start, end, allday } = displaySelection

    // all day인 경우 타임라인에 표시하지 않음 (DayAllday 영역에 표시)
    if (allday) return null

    // Always recalculate conflict state to ensure accuracy
    const conflictDetected = checkSelectionConflict(start, end, allday)

    // 현재 날짜에 대한 selection 부분 계산
    const dateSelection = getSelectionForDate(start, end, currentDate, allday)

    // 현재 날짜와 겹치지 않는 경우 표시하지 않음
    if (!dateSelection) return null

    const { start: displayStart, end: displayEnd } = dateSelection

    // 위치 및 크기 계산 (현재 날짜 기준)
    const startMinutes = displayStart.getHours() * 60 + displayStart.getMinutes()
    const endMinutes = displayEnd.getHours() * 60 + displayEnd.getMinutes()
    const durationMinutes = endMinutes - startMinutes
    const startY = (startMinutes / MINUTES_PER_SLOT) * SLOT_HEIGHT
    const height = (durationMinutes / MINUTES_PER_SLOT) * SLOT_HEIGHT

    // 시간 표시 (전체 selection의 시작/종료 시간)
    const timeDisplayStart = formatTimeWithDate(
      start,
      start,
      end,
      currentDate,
      dateFormat,
      timeFormat,
      true,
    )
    const timeDisplayEnd = formatTimeWithDate(
      end,
      start,
      end,
      currentDate,
      dateFormat,
      timeFormat,
      false,
    )

    // Conflict message
    const conflictMessage = attendeesCount > 0 ? getConflictMessage(start, end, allday) : ''

    // Event handlers for single-mode (drag/resize)
    const handleMouseDown = (e: React.MouseEvent) => {
      e.stopPropagation()
      const rect = e.currentTarget.getBoundingClientRect()
      const zone = getInteractionZone(e.clientY, rect.top, height)
      onSelectionMouseDown(e.nativeEvent, zone)
    }

    const handleTouchStart = (e: React.TouchEvent) => {
      e.stopPropagation()
      if (e.touches.length !== 1) return
      const touch = e.touches[0]
      const rect = e.currentTarget.getBoundingClientRect()
      const zone = getInteractionZone(touch.clientY, rect.top, height)
      onSelectionTouchStart(e.nativeEvent, zone)
    }

    return (
      <SelectionRect
        key="single-selection"
        start={start}
        end={end}
        index={0}
        multiMode={false}
        hasConflict={conflictDetected}
        eventTitle={eventTitle}
        conflictMessage={conflictMessage}
        startY={startY}
        height={height}
        timeDisplayStart={timeDisplayStart}
        timeDisplayEnd={timeDisplayEnd}
        onRemove={null}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      />
    )
  }

  return (
    <div className={styles.dayBox} ref={dayBoxRef}>
      {/* 상단 블록 (Empty, Day Info, Allday Container) */}
      <div className={styles.headerBlock} ref={headerBlockRef}>
        <TimezoneNameBlock targetDate={currentDate} />

        <div className={styles.headerContent}>
          <DayHeader date={currentDate} isToday={isToday} onDoubleClick={() => {}} />

          <DayAllday
            allday={getAllDayEventsWithSelection as any[]}
            date={currentDate}
            onDoubleClick={() => {}}
            onEventClick={() => {}}
            onEventDoubleClick={() => {}}
            readOnly={true} // SchedulingAssistant에서는 이벤트 클릭/드래그 불필요 - 시간 선택만 가능
          />
        </div>
      </div>

      {/* 하단 컨테이너 (Period, Timeline) */}
      <div
        className={styles.contentContainer}
        style={{
          // Mobile: Disable scroll when dragging/resizing selection rect
          overflowY:
            isMobileView &&
            interactionMode &&
            ['moving', 'resizing-top', 'resizing-bottom'].includes(interactionMode)
              ? 'hidden'
              : 'auto',
        }}
      >
        <div
          className={styles.scrollContainer}
          ref={scrollContainerRef}
          style={{
            // Mobile: Disable scroll when dragging/resizing selection rect
            overflowY:
              isMobileView &&
              interactionMode &&
              ['moving', 'resizing-top', 'resizing-bottom'].includes(interactionMode)
                ? 'hidden'
                : 'auto',
          }}
        >
          <PeriodView targetDate={currentDate} />

          <div
            className={styles.timelineContainer}
            ref={timeLineContainerRef}
            onMouseDown={onTimelineMouseDown}
            onMouseMove={onTimelineMouseMove}
            onMouseUp={onTimelineMouseUp}
            onTouchStart={onTimelineTouchStart}
            onTouchMove={onTimelineTouchMove}
            onTouchEnd={onTimelineTouchEnd}
          >
            <TimeLineView // DayView
              timeline={timeline as EventInstance[]}
              date={currentDate}
              mode="schedulingAssistant" // Enable scheduling assistant mode for separate busy blocks layer
              busyBlocks={mergedBusyBlocks} // Merged attendee busy times for red overlay
              readOnly={true} // SchedulingAssistant에서는 이벤트 클릭/드래그 불필요 - 시간 선택만 가능
            />
            {renderTempSelectionArea()} {/* 드래그 중 임시 영역 (selecting 모드) */}
            {multiMode ? renderMultipleSelections() : renderSelectionTimeRectOverlay()}{' '}
            {/* 최종 확정된 선택 영역 (moving/resizing 모드 또는 확정 후) */}
          </div>

          {isToday && (
            <TimeMark currentTime={currentTime} getCurrentTimePosition={getCurrentTimePosition} />
          )}
        </div>
      </div>
    </div>
  )
}

export default SchedulingDayBox
